import React, { ReactNode } from "react";
import {
  Routes,
  Route,
  Navigate,
  NavigateFunction,
  Params,
} from "react-router-dom";
import { CategoryService, MembershipService } from "../../shared/services";
import { Subject, takeUntil } from "rxjs";
import MembershipStore from "./MembershipStore";
import { Provider, inject, observer } from "mobx-react";
import { UpsertMembershipActiveStep } from "./Membership.enum";
import MembershipProtectedRoute from "./MembershipProtectedRoute";
import MembershipPricingPage from "./components/PricingPage";
import OptionsPage from "./components/OptionsPage";
import {
  AuthService,
  FlexContainer,
  Loader,
  RedButton,
  SapphireBlueButton,
  ToastService,
  UtilityMethods,
  withRouter,
} from "@mindmemobile/mm-shared-global";
import {
  Header,
  ItemDetailPage,
  ItemHeader,
  Layout,
  MediaBoxes,
  SideBar,
} from "../../shared/components";
import { RolesAndPermissionStore } from "../../shared/store";
import { ProductDetailWrapper } from "../../shared/styles";
import {
  CountryWithStateModel,
  ItemDetailInputModel,
  MediaModel,
} from "../../shared/models";
import { toJS } from "mobx";
import { MediaTypes } from "../../shared/enums";
import FullLibraryView from "../../shared/components/FullLibraryView";
import { getMerchantAccountStore } from "../merchantAccounts/MerchantAccountStore";

interface IProps {
  membershipStore?: MembershipStore;
  navigate: NavigateFunction;
  params: Readonly<Params<string>>;
  rolesAndPermissionStore: RolesAndPermissionStore;
  countryDataLoaded: boolean;
  countriesWithState: CountryWithStateModel[];
}

type IState = {
  showLibraryView: boolean;
  showUpload?: boolean;
};

export const getCategoryService = (() => {
  let instance: CategoryService | null = null;
  return () => {
    if (!instance) {
      instance = new CategoryService();
    }
    return instance;
  };
})();

@inject("membershipStore")
@observer
class MembershipRoutes extends React.Component<IProps, IState> {
  private loader: Loader = new Loader(false);
  private $destroy: Subject<boolean> = new Subject<boolean>();
  private disposeSurchargeDefault?: () => void;

  constructor(props: IProps) {
    super(props);
    this.state = {
      showLibraryView: false,
      showUpload: false,
    };
  }

  toggleLibraryView = (show: boolean, fromUpload: boolean = false) => {
    this.setState(() => ({
      showLibraryView: show,
      showUpload: fromUpload,
    }));
  };

  componentDidMount(): void {
    this.props.membershipStore?.reset();

    if (this.membershipId) {
      this.loader.show();
      MembershipService.getMembership(
        AuthService.authData.accountId,
        this.membershipId,
      )
        .pipe(takeUntil(this.$destroy))
        .subscribe({
          next: (result) => {
            if (!result.result) {
              ToastService.showError("Error in loading membership details");
            } else {
              this.loader.hide();
              this.props.membershipStore?.constructStoreFromAPI(result.result);
            }
          },
          error: () => {
            ToastService.showError("Error in loading membership details");
          },
        });
    } else {
      this.setDefaultSurcharge();
    }
  }

  /* A new membership picks up the surcharge setting of the merchant account it
     will be sold through. Only runs when creating: an existing membership
     keeps the setting it was saved with. */
  setDefaultSurcharge = () => {
    this.disposeSurchargeDefault =
      getMerchantAccountStore().whenItemSurchargeDefaultKnown((surcharge) => {
        const { membershipStore } = this.props;
        if (!membershipStore) return;
        const updated = ItemDetailInputModel.withMerchantSurchargeDefault(
          membershipStore.detailPage,
          surcharge,
        );
        if (updated) membershipStore.updateDetailsPage(updated);
      });
  };

  onCancel() {
    if (
      this.props.membershipStore?.activeStep ===
      UpsertMembershipActiveStep.Pricing
    ) {
      this.onBackOrNext(false);
      return;
    }
    this.props.navigate(AuthService.prefixRoute + "items");
  }

  get membershipId() {
    const split = ((this.props.params && this.props.params["*"]) || "").split(
      "/",
    );
    const id = split[split.length - 1];
    if (UtilityMethods.isValidObjectId(id)) {
      return id;
    }
    return null;
  }

  componentWillUnmount(): void {
    this.$destroy.next(true);
    this.disposeSurchargeDefault?.();
  }

  onBackOrNext(isNext: boolean) {
    if (this.loader.showLoader) return;
    let queryParam: string = "";
    if (this.membershipId) {
      queryParam = "/" + this.membershipId;
    }
    if (isNext) {
      const success = this.props.membershipStore?.validate();
      if (!success) return;
    }
    switch (this.props.membershipStore?.activeStep) {
      case UpsertMembershipActiveStep.Detail:
        this.props.membershipStore?.updateActiveStep(
          UpsertMembershipActiveStep.Pricing,
        );
        this.props.navigate(`/app/membership/pricing${queryParam}`);
        break;
      case UpsertMembershipActiveStep.Pricing:
        isNext
          ? this.props.membershipStore?.updateActiveStep(
              UpsertMembershipActiveStep.Options,
            )
          : this.props.membershipStore?.updateActiveStep(
              UpsertMembershipActiveStep.Detail,
            );
        isNext
          ? this.props.navigate(`/app/membership/options${queryParam}`)
          : this.props.navigate(`/app/membership/detail${queryParam}`);
        break;

      case UpsertMembershipActiveStep.Options:
        this.props.membershipStore?.updateActiveStep(
          UpsertMembershipActiveStep.Pricing,
        );
        this.props.navigate(`/app/membership/pricing${queryParam}`);
        break;
    }
  }

  stepConfig = {
    [UpsertMembershipActiveStep.Detail]: {
      name: "detail",
      url: "/app/membership/detail",
    },
    [UpsertMembershipActiveStep.Pricing]: {
      name: "pricing",
      url: "/app/membership/pricing",
    },
    [UpsertMembershipActiveStep.Options]: {
      name: "options",
      url: "/app/membership/options",
    },
  };

  onTabChange = (key: UpsertMembershipActiveStep) => {
    const queryParam = this.membershipId ? `/${this.membershipId}` : "";

    const validationResult =
      this.props.membershipStore?.validatePreviousSteps(key);
    if (!validationResult) {
      ToastService.showError("Unable to validate steps.");
      return;
    }

    const { isValid, errorStep } = validationResult;

    if (!isValid && errorStep) {
      ToastService.showError(
        `Please fill all the required fields in membership ${this.stepConfig[errorStep].name} before proceeding.`,
      );
      this.props.membershipStore?.updateActiveStep(errorStep);
      this.props.navigate(`${this.stepConfig[errorStep].url}${queryParam}`);
      return;
    }

    this.props.membershipStore?.updateActiveStep(key);
    this.props.navigate(`${this.stepConfig[key].url}${queryParam}`);
  };

  onSave = (isSaveAndExit: boolean = false) => {
    if (this.loader.showLoader) return;
    if (!this.props.membershipStore) return;

    if (isSaveAndExit) {
      const validationResult =
        this.props.membershipStore?.validatePreviousSteps(
          UpsertMembershipActiveStep.Options,
        );
      if (!validationResult) {
        ToastService.showError("Validation failed.");
        return;
      }

      const { isValid, errorStep } = validationResult;

      if (!isValid && errorStep) {
        ToastService.showError(
          `Please fill all the required fields in membership ${this.stepConfig[errorStep].name} before saving.`,
        );
        this.props.membershipStore?.updateActiveStep(errorStep);
        this.props.navigate(
          `${this.stepConfig[errorStep].url}/${this.membershipId || ""}`,
        );
        return;
      }

      const isOptionsValid = this.props.membershipStore?.validate(
        UpsertMembershipActiveStep.Options,
      );
      if (!isOptionsValid) {
        ToastService.showError(
          "Please fill all the required fields in the Options step before saving.",
        );
        this.props.membershipStore?.updateActiveStep(
          UpsertMembershipActiveStep.Options,
        );
        this.props.navigate(
          `${this.stepConfig[UpsertMembershipActiveStep.Options].url}/${this.membershipId || ""}`,
        );
        return;
      }
    } else {
      const success = this.props.membershipStore?.validate();
      if (!success) return;
    }

    const request = this.props.membershipStore.createAPIModel();

    if (this.props.membershipStore?.isSaveUpdateInProgress) return;
    this.props.membershipStore?.toggleSaveUpdateInProgress();

    if (this.membershipId) {
      request.id = this.membershipId;
      MembershipService.update(request).subscribe({
        next: (response) => {
          if (response) {
            ToastService.showSuccess("Membership updated Successfully");
            this.props.membershipStore?.toggleSaveUpdateInProgress();
            if (isSaveAndExit) {
              this.props.navigate("/app/items");
            }
          }
        },
        error: () => {
          ToastService.showError("Membership update failed!");
          this.props.membershipStore?.toggleSaveUpdateInProgress();
        },
      });
    } else {
      MembershipService.save(request).subscribe({
        next: (response) => {
          if (response.id) {
            ToastService.showSuccess("Saved Successfully");
            this.props.membershipStore?.toggleSaveUpdateInProgress();
            if (isSaveAndExit) {
              this.props.navigate("/app/items");
            }
          }
        },
        error: () => {
          ToastService.showError("Error in saving");
          this.props.membershipStore?.toggleSaveUpdateInProgress();
        },
      });
    }
  };

  get showNextButton(): boolean {
    return (
      this.props.membershipStore?.activeStep !==
      UpsertMembershipActiveStep.Options
    );
  }

  get showBackbutton(): boolean {
    return (
      this.props.membershipStore?.activeStep !==
      UpsertMembershipActiveStep.Detail
    );
  }

  get headerCancelButtonCaption(): string {
    return this.props.membershipStore?.activeStep ==
      UpsertMembershipActiveStep.Pricing
      ? "Back"
      : "";
  }

  onSelectLibraryMedia(media: MediaModel) {
    this.props.membershipStore?.updateDetailsPage(
      new ItemDetailInputModel({
        ...this.props.membershipStore?.detailPage,
        medias: [...this.props.membershipStore?.detailPage.medias, media],
        showLibrary: false,
      }),
    );
  }

  deleteMedia(medias: MediaModel[]) {
    this.props.membershipStore?.updateDetailsPage(
      new ItemDetailInputModel({
        ...this.props.membershipStore?.detailPage,
        medias,
      }),
    );
  }

  onFavourite(media: MediaModel) {
    const updatedMedia = (
      toJS(this.props.membershipStore?.detailPage?.medias) || []
    ).map((x) => {
      if (x.mediaId === media.mediaId) {
        return { ...x, isFavourite: true } as MediaModel;
      } else if (x.isFavourite) {
        return { ...x, isFavourite: false } as MediaModel;
      }
      return x;
    });
    const index = updatedMedia.findIndex((x) => x.mediaId === media.mediaId);
    if (index !== -1) {
      const [favouritedMedia] = updatedMedia.splice(index, 1);
      updatedMedia.unshift(favouritedMedia);
    }
    this.props.membershipStore?.updateDetailsPage(
      new ItemDetailInputModel({
        ...this.props.membershipStore?.detailPage,
        medias: [...updatedMedia],
      }),
    );
  }

  onDelete(media: MediaModel) {
    const updatedMedia = (
      toJS(this.props.membershipStore?.detailPage?.medias) || []
    ).map((x) => x);
    const index = updatedMedia.findIndex((x) => x.mediaId === media.mediaId);
    updatedMedia.splice(index, 1);
    this.deleteMedia(updatedMedia);
  }

  updateMedia(medias: MediaModel[]) {
    this.props.membershipStore?.updateDetailsPage(
      new ItemDetailInputModel({
        ...this.props.membershipStore?.detailPage,
        medias: [...this.props.membershipStore?.detailPage.medias, ...medias],
      }),
    );
  }

  get footerButtons() {
    return (
      <FlexContainer
        alignItems="center"
        justifyContent="center"
        spacing={15}
        size={0}
        padding="10px 0px 0px"
      >
        <RedButton height="40px" onClick={() => this.onCancel()}>
          Cancel
        </RedButton>
        {this.showBackbutton && (
          <SapphireBlueButton
            height="40px"
            onClick={() => this.onBackOrNext(false)}
          >
            Back
          </SapphireBlueButton>
        )}

        {this.showNextButton && (
          <SapphireBlueButton
            height="40px"
            onClick={() => this.onBackOrNext(true)}
          >
            Next
          </SapphireBlueButton>
        )}
      </FlexContainer>
    );
  }
  get routes() {
    return (
      <Routes>
        <Route
          path={`/detail/:id?`}
          element={
            <MembershipProtectedRoute currentRoute="detail">
              <>
                <ProductDetailWrapper>
                  <ItemHeader header="Membership Details" />
                  <ItemDetailPage
                    isProduct={false}
                    itemDetail={this.props.membershipStore?.detailPage}
                    onUpdate={(detail: ItemDetailInputModel) =>
                      this.props.membershipStore?.updateDetailsPage(detail)
                    }
                    toggleLibrary={(show: boolean, fromUpload: boolean) =>
                      this.toggleLibraryView(show, fromUpload)
                    }
                    showLibraryView={this.state.showLibraryView}
                  />
                </ProductDetailWrapper>
              </>
            </MembershipProtectedRoute>
          }
        />
        <Route
          path={`/pricing/:id?`}
          element={
            <MembershipProtectedRoute currentRoute="pricing">
              <>
                <MembershipPricingPage />
                {/* {this.footerButtons} */}
              </>
            </MembershipProtectedRoute>
          }
        />

        <Route
          path={`/options/:id?`}
          element={
            <MembershipProtectedRoute currentRoute="options">
              <>
                <OptionsPage
                  sourceId={this.membershipId}
                  countriesWithState={this.props.countriesWithState}
                />
                {/* {this.footerButtons} */}
              </>
            </MembershipProtectedRoute>
          }
        />
        <Route path="*" element={<Navigate to="/app/membership/detail" />} />
      </Routes>
    );
  }

  render(): ReactNode {
    if (!this.props.countryDataLoaded) {
      return <>{new Loader().loaderComponent}</>;
    }
    if (this.state.showLibraryView) {
      return (
        <FullLibraryView
          multiple={true}
          medias={toJS(this.props.membershipStore?.detailPage.medias || [])}
          onUpload={(medias: MediaModel[]) => this.updateMedia(medias)}
          onDelete={(media: MediaModel) => this.onDelete(media)}
          onSelectLibraryMedia={(media) =>
            this.onSelectLibraryMedia(MediaModel.mediaItemToMediaModel(media))
          }
          onFavourite={(media: MediaModel) => this.onFavourite(media)}
          allowedMediaTypes={[MediaTypes.Image, MediaTypes.Video]}
          toggleLibrary={(show) => this.toggleLibraryView(show)}
          showLibrary={this.state.showLibraryView}
          updateMedia={(medias: MediaModel[]) => this.updateMedia(medias)}
          showUpload={this.state.showUpload}
        />
      );
    }
    return (
      <Provider categoryService={getCategoryService()}>
        <Layout
          sideBarComponent={
            <SideBar
              activeStep={
                this.props.membershipStore?.activeStep?.toString() || ""
              }
              showVariant={false}
              onTabChange={(key: UpsertMembershipActiveStep) =>
                this.onTabChange(key)
              }
            />
          }
          headerCaption={""}
          cancelClick={() => this.onCancel()}
          nextButtonCaption={this.showNextButton ? "Next" : ""}
          nextClick={() => this.onBackOrNext(true)}
          showBackButton={this.showBackbutton}
          showNextButton={this.showNextButton}
          backClick={() => this.onBackOrNext(false)}
          saveAndExitClick={() => this.onSave(true)}
          showSaveAndExit={this.membershipId ? true : false}
          disableSaveAndExit={
            this.loader.showLoader ||
            !!this.props.membershipStore?.isSaveUpdateInProgress
          }
          headerComponent={
            <Header
              headerCaption={"CREATE YOUR ITEM"}
              showSaveButton={!this.showNextButton}
              saveAndExitButtonCaption={
                this.showNextButton ? "NEXT" : "SAVE & EXIT"
              }
              showSaveAndExitButton={true}
              disableSaveButton={
                this.props.membershipStore?.isSaveUpdateInProgress ||
                !this.membershipId
              }
              disableSaveAndExitButton={
                this.loader.showLoader ||
                !!this.props.membershipStore?.isSaveUpdateInProgress
              }
              saveClick={() => this.onSave()}
              cancelClick={() => this.onCancel()}
              cancelButtonCaption={this.headerCancelButtonCaption}
              saveAndExitClick={() => {
                if (this.loader.showLoader) return;
                this.showNextButton
                  ? this.onBackOrNext(this.showNextButton)
                  : this.onSave(true);
              }}
            />
          }
          showHeader={false}
          showCancelButton={false}
          showFooter={!this.loader.showLoader}
          // noScroll={true}
        >
          {this.loader.showLoader ? this.loader.loaderComponent : this.routes}
        </Layout>
      </Provider>
    );
  }
}

export default withRouter(MembershipRoutes);
